import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCalendar,
  faChevronDown,
  faChevronUp,
  faSave,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import FileUpload from './FileUpload';

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .max(100, 'Title cannot exceed 100 characters'),
  amount: Yup.number()
    .required('Amount is required')
    .positive('Amount must be positive')
    .typeError('Amount must be a number'),
  date: Yup.date().required('Date is required'),
  description: Yup.string().max(500, 'Description cannot exceed 500 characters'),
  categoryId: Yup.string().required('Category is required'),
  splitMethod: Yup.string()
    .required('Split method is required')
    .oneOf(
      ['SALARY_PERCENTAGE', 'FIFTY_FIFTY', 'NO_REIMBURSEMENT', 'FULL_RECLAIM'],
      'Invalid split method'
    ),
  paidByUserId: Yup.string().required('Please select who paid'),
  labelIds: Yup.array().of(Yup.string()),
});

const ExpenseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isEditing = Boolean(id);

  const [expense, setExpense] = useState(null);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [users, setUsers] = useState([]);
  const [expenseFiles, setExpenseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    title: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    categoryId: '',
    splitMethod: 'SALARY_PERCENTAGE',
    paidByUserId: '',
    labelIds: [],
  });

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesRes, labelsRes, usersRes] = await Promise.all([
          api.get('/categories'),
          api.get('/labels'),
          api.get('/users'),
        ]);

        if (cancelled) return;

        const nextUsers = usersRes.data.data || [];
        setCategories(categoriesRes.data.data || []);
        setLabels(labelsRes.data.data || []);
        setUsers(nextUsers);

        if (!isEditing) {
          const defaultPayer =
            nextUsers.find((user) => user.id === currentUser?.id) || nextUsers[0];
          setInitialFormValues((prev) => ({
            ...prev,
            paidByUserId: prev.paidByUserId || defaultPayer?.id || '',
            categoryId: prev.categoryId || categoriesRes.data.data?.[0]?.id || '',
          }));
        } else {
          const expenseRes = await api.get(`/expenses/${id}`);
          if (cancelled) return;
          if (!expenseRes.data.success) {
            throw new Error('Failed to load expense details');
          }
          const expenseData = expenseRes.data.data;
          const formattedDate = new Date(expenseData.date).toISOString().split('T')[0];
          const labelIds = expenseData.Labels
            ? expenseData.Labels.map((label) => label.id)
            : [];
          setExpense({
            ...expenseData,
            date: formattedDate,
            labelIds,
          });
          setExpenseFiles(expenseData.files || []);
          setShowDetails(
            Boolean(expenseData.description) || (labelIds && labelIds.length > 0)
          );
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching expense form data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // Intentionally depend on stable user id, not the whole user object
  }, [id, isEditing, currentUser?.id]);

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const payload = {
        title: values.title.trim(),
        amount: values.amount,
        date: values.date,
        description: values.description || '',
        categoryId: values.categoryId,
        paidByUserId: values.paidByUserId,
        splitMethod: values.splitMethod,
        labelIds: values.labelIds || [],
      };

      const response = isEditing
        ? await api.put(`/expenses/${id}`, payload)
        : await api.post('/expenses', payload);

      if (response.data.success) {
        const savedId = response.data.data?.id || id;
        navigate(savedId ? `/expenses/${savedId}` : '/expenses');
      } else {
        setStatus({ error: response.data.message || 'Failed to save expense' });
      }
    } catch (err) {
      console.error('Error saving expense:', err);
      setStatus({
        error: err.response?.data?.message || 'Failed to save expense. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading form…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button as={Link} to="/expenses" variant="outline-danger">
          Back to expenses
        </Button>
      </Alert>
    );
  }

  return (
    <div className="expense-form-page">
      <div className="page-header">
        <div>
          <Link to="/expenses" className="small text-decoration-none d-inline-flex align-items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faArrowLeft} />
            Expenses
          </Link>
          <p className="eyebrow">{isEditing ? 'Edit' : 'New'}</p>
          <h1>{isEditing ? 'Edit expense' : 'Add expense'}</h1>
          <p className="text-muted mb-0">
            {isEditing
              ? 'Update the details below.'
              : 'Title, amount, who paid, and split — then save.'}
          </p>
        </div>
      </div>

      <Formik
        initialValues={isEditing && expense ? expense : initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          status,
        }) => (
          <Form onSubmit={handleSubmit} className="expense-form-layout">
            {status?.error && (
              <Alert variant="danger" className="mb-3">
                {status.error}
              </Alert>
            )}

            <section className="panel mb-3">
              <div className="panel-body">
                <Form.Group className="mb-3">
                  <Form.Label>What was it?</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="e.g. Water bill"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.title && !!errors.title}
                    autoFocus={!isEditing}
                  />
                  <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                </Form.Group>

                <Row className="g-3">
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Amount</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>£</InputGroup.Text>
                        <Form.Control
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          name="amount"
                          placeholder="0.00"
                          value={values.amount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.amount && !!errors.amount}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.amount}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="date"
                        value={values.date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.date && !!errors.date}
                      />
                      <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {values.date && new Date(values.date) > new Date() && (
                  <p className="small text-info mt-2 mb-0">
                    <FontAwesomeIcon icon={faCalendar} className="me-1" />
                    Planned future expense
                  </p>
                )}
              </div>
            </section>

            <section className="panel mb-3">
              <div className="panel-body">
                <Form.Group className="mb-3">
                  <Form.Label>Who paid?</Form.Label>
                  <div className="expense-choice-row">
                    {users.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className={`expense-choice${
                          values.paidByUserId === user.id ? ' active' : ''
                        }`}
                        onClick={() => setFieldValue('paidByUserId', user.id)}
                      >
                        {user.id === currentUser?.id ? 'Me' : user.displayName}
                      </button>
                    ))}
                  </div>
                  {touched.paidByUserId && errors.paidByUserId && (
                    <div className="text-danger small mt-1">{errors.paidByUserId}</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Split</Form.Label>
                  <div className="expense-choice-row expense-choice-wrap">
                    {[
                      { value: 'SALARY_PERCENTAGE', label: 'Salary %' },
                      { value: 'FIFTY_FIFTY', label: '50/50' },
                      { value: 'FULL_RECLAIM', label: 'Full reclaim' },
                      { value: 'NO_REIMBURSEMENT', label: 'No reclaim' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`expense-choice${
                          values.splitMethod === option.value ? ' active' : ''
                        }`}
                        onClick={() => setFieldValue('splitMethod', option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {values.splitMethod === 'SALARY_PERCENTAGE' &&
                      'Reclaim uses each person’s salary share for that month.'}
                    {values.splitMethod === 'FIFTY_FIFTY' &&
                      'Other person owes half.'}
                    {values.splitMethod === 'FULL_RECLAIM' &&
                      'Other person owes the full amount.'}
                    {values.splitMethod === 'NO_REIMBURSEMENT' &&
                      'Personal spend — no reclaim.'}
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    name="categoryId"
                    value={values.categoryId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.categoryId && !!errors.categoryId}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.categoryId}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </section>

            <section className="panel mb-3">
              <button
                type="button"
                className="expense-details-toggle"
                onClick={() => setShowDetails((v) => !v)}
                aria-expanded={showDetails}
              >
                <span>Notes & labels</span>
                <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} />
              </button>
              {showDetails && (
                <div className="panel-body pt-0">
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={values.description || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.description && !!errors.description}
                      placeholder="Optional"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.description}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {labels.length > 0 && (
                    <Form.Group>
                      <Form.Label>Labels</Form.Label>
                      <div className="expense-label-chips">
                        {labels.map((label) => {
                          const active = values.labelIds.includes(label.id);
                          return (
                            <button
                              key={label.id}
                              type="button"
                              className={`expense-chip${active ? ' active' : ''}`}
                              onClick={() => {
                                if (active) {
                                  setFieldValue(
                                    'labelIds',
                                    values.labelIds.filter((item) => item !== label.id)
                                  );
                                } else {
                                  setFieldValue('labelIds', [...values.labelIds, label.id]);
                                }
                              }}
                            >
                              <span
                                className="expense-chip-dot"
                                style={{ background: label.color || '#6c757d' }}
                              />
                              {label.name}
                            </button>
                          );
                        })}
                      </div>
                    </Form.Group>
                  )}
                </div>
              )}
            </section>

            {isEditing && expense && (
              <section className="panel mb-3">
                <div className="panel-body">
                  <FileUpload
                    expenseId={expense.id}
                    existingFiles={expenseFiles}
                    onFilesUploaded={setExpenseFiles}
                  />
                </div>
              </section>
            )}

            <div className="expense-form-actions">
              {isEditing ? (
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => {
                    if (window.confirm('Delete this expense?')) {
                      api
                        .delete(`/expenses/${id}`)
                        .then(() => navigate('/expenses'))
                        .catch((err) => {
                          console.error('Error deleting expense:', err);
                          alert('Failed to delete expense.');
                        });
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                  Delete
                </Button>
              ) : (
                <Button as={Link} to="/expenses" variant="outline-secondary">
                  Cancel
                </Button>
              )}
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {isEditing ? 'Save changes' : 'Save expense'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ExpenseForm;
